/**
 * O EMPREENDIMENTO aberto — o endereço `/inc/<e>/emp/<d>`.
 *
 * Até 09/10/2026 isto era a TELA INTERMEDIÁRIA com os cenários listados (EL-176). Pedido
 * literal do lote de 09/10/2026, item 4: "Elimine a tela intermediária que existe com os
 * cenários listados. Leve isso para dentro da tela que já mostra os dados do cenário." A lista
 * e as ações foram para o seletor no título do cenário (EL-003, `ui/seletor-cenario.ts`).
 *
 * O endereço continua valendo (links e favoritos antigos, o cartão do empreendimento) e
 * agora RESOLVE: abre o cenário padrão — o último que esta pessoa abriu aqui, neste
 * navegador; senão o mais antigo — TROCANDO a entrada do histórico, para o "voltar" não
 * cair num pingue-pongue. Sem cenário ativo, mostra o estado vazio: criar o primeiro (quem
 * pode editar) e os arquivados, se houver. Sem as áreas do simulador na barra lateral — não
 * há cenário sobre o qual elas falem.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, carregando, vazio } from '../casca';
import type { Vista } from '../casca';
import { ir } from '../rotas';
import { linhasArquivados } from '../ui/seletor-cenario';
import { escolherCenarioPadrao } from '../cenarios-regras';
import { acoesDeCenario } from './acoes-cenario';
import { entregarListaDeCenarios, ultimoCenarioAberto } from './cenario';
import {
  listarCenarios, listarCenariosArquivados, listarEmpreendimentos, listarEmpresas,
} from '../../../../packages/dados/src/repositorios';
import { podeEditar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';

export async function telaEmpreendimento(perfil: Perfil, empresaId: string, empreendimentoId: string): Promise<Vista> {
  const [empresas, irmaos, cenarios] = await Promise.all([
    listarEmpresas(),
    listarEmpreendimentos(empresaId),
    listarCenarios(empreendimentoId),
  ]);
  const empresa = empresas.find((e) => e.id === empresaId);
  const emp = irmaos.find((e) => e.id === empreendimentoId);
  /* Mesma regra do EL-174: endereço que não resolve avisa, em vez de mostrar uma casca
     vazia com nome genérico — a tela do cenário usaria "Empreendimento" calada. */
  if (empresa === undefined) throw new Error('Esta incorporadora não existe mais, ou saiu do seu acesso.');
  if (emp === undefined) throw new Error('Este empreendimento não existe mais, ou foi arquivado.');

  const trilha: Vista['trilha'] = [
    { texto: 'Incorporadoras', rota: { nome: 'incorporadoras' } },
    { texto: empresa.nome, rota: { nome: 'empreendimentos', empresaId } },
    { texto: emp.nome },
  ];

  const padrao = escolherCenarioPadrao(cenarios, ultimoCenarioAberto(empreendimentoId));
  if (padrao !== null) {
    /* A lista acabou de ser lida: a tela do cenário a reaproveita para o seletor. */
    entregarListaDeCenarios(empreendimentoId, cenarios);
    /* `ir` redesenha na hora (outra geração do desenho); esta vista nunca chega à tela. */
    ir({ nome: 'cenario', cenarioId: padrao.id }, { substituir: true });
    return { trilha, corpo: carregando('o cenário'), ativo: 'empreendimentos', empresaId };
  }

  /* ---- estado vazio ---- */
  /* ADM-2: a situação lida AGORA (esta lista) vale sobre o perfil em memória — ver `sessao.ts`. */
  const editavel = podeEditar(perfil, empresaId, empresa.ativa);
  const veArquivados = editavel || perfil.isAdminHype;
  const arquivados = veArquivados ? await listarCenariosArquivados(empreendimentoId).catch(() => []) : [];
  const acoes = acoesDeCenario({ empresaId, empreendimentoId, ativos: () => [], abertoId: null, versaoDoAberto: () => null });

  /* "ainda" só é verdade quando nunca houve cenário: com arquivados, a frase aponta para eles. */
  const soArquivados = arquivados.length > 0;
  const corpo = h('div', {});
  corpo.appendChild(cabecalhoDeTela(emp.nome, 'Nenhum cenário ativo neste empreendimento.'));
  corpo.appendChild(
    vazio(
      editavel
        ? soArquivados
          ? 'Nenhum cenário ativo. Crie um novo ou restaure um dos arquivados abaixo.'
          : 'Nenhum cenário ainda. Crie o primeiro para começar a simular.'
        : 'Nenhum cenário ativo neste empreendimento.',
      editavel
        ? h('button', { class: 'btn pri', 'data-el': 'EL-177', onclick: () => acoes.novo() },
            h('span', { html: icone.mais(15) }), soArquivados ? 'Novo cenário' : 'Criar o primeiro cenário')
        : null,
    ),
  );
  if (arquivados.length > 0) {
    corpo.appendChild(
      h(
        'div',
        { class: 'card cen-arq-card', 'data-el': 'EL-194' },
        h('h3', {}, h('span', { html: icone.arquivar(15) }), `Arquivados (${arquivados.length})`),
        linhasArquivados(arquivados, { editar: editavel, excluirDefinitivo: perfil.isAdminHype }, acoes),
      ),
    );
  }

  return {
    trilha,
    corpo,
    ativo: 'empreendimentos',
    /* Deixa a incorporadora registrada na casca: é o que faz o item "Documentos" da barra
       lateral saber de quem são as pastas, sem perguntar. */
    empresaId,
  };
}
